import { Box, Button, ButtonGroup, Grid } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../hooks/UserContext";
import "react-csv-importer/dist/index.css";
import { ManualEntryForm } from "./ImportForm_Manual";

import { ImportOption, ImportOptionChoices } from "../../utilities/types";

import { CSVImportForm } from "./ImportForm_CSV";
import DealSelectionAutocomplete from "../helpers/DealSelectionAutocomplete";
import CustomAutocomplete from "../helpers/CustomAutocomplete";
import { LoadingComponent } from "../helpers/LoadingComponent";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { API, Branch, Company, Contact, Deal, Grower, Job } from "../../api";
import { INVALID_USERCONTEXT } from "../../utilities/constants";
import { Oops } from "../../utilities/oops";
import { ProagricaImportForm } from "./ImportForm_Proagrica";
import { External1ImportForm } from "./ImportForm_External1";
import { GeoData } from "@rogoag/shared-ts";
import { GeoDataImportForm } from "./ImportForm_GeoData";

interface JobSubmissionFormProps {
	initialState?: Job;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function JobSubmissionForm(props: JobSubmissionFormProps) {
	const userContext = useContext(UserContext);
	const [user, setUser] = useState<Contact>();

	const [deals, setDeals] = useState<Deal[]>([]);
	const [selectedDeals, setSelectedDeals] = useState<Deal[]>([]);
	const [growers, setGrowers] = useState<Grower[]>([]);
	const [, setSelectedGrowers] = useState<Grower[]>([]);
	const [companyOptions, setCompanyOptions] = useState<Company[]>([]);
	const [branches, setBranches] = useState<Branch[]>([]);
	const [selectedBranches, setSelectedBranches] = useState<Branch[]>([]);
	const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
	const [salespersons, setSalespersons] = useState<Contact[]>([]);
	// const [growerOptions, setGrowerOptions] = useState<{ label: string, id: string }[]>([]);
	const [loading, setLoading] = useState(false);
	const [isZone, setIsZone] = useState(false);
	const [importOptions, setImportOptions] = useState<ImportOption[]>([]);
	const [dataImportOption, setDataImportOption] = useState<ImportOption | null>(null);
	const [lastCropOptions, setLastCropOptions] = useState<string[]>([]);
	const [nextCropOptions, setNextCropOptions] = useState<string[]>([]);
	const [, setShapefileFields] = useState<string[]>([]);
	const [originalShapefiles] = useState<File[]>([]);

	const [, setSelectableFiles] = useState<File[]>([]);

	useEffect(() => {
		for (const deal of deals) {
			if (selectedDeals.includes(deal)) {
				// console.log(deal);
				if (deal.type === "Zone") {
					setIsZone(true);
				} else {
					setIsZone(false);
				}
				break;
			}
		}
	}, []);

	useEffect(() => {
		const getShapeData = async () => {
			if (!originalShapefiles.length) {
				return;
			}

			const buffer = Buffer.from(await originalShapefiles[0].arrayBuffer());
			const geoJSON = await GeoData.toGeoJSON(buffer, "SHP");
			const properties = geoJSON.features[0].properties;
			if (!properties) {
				setShapefileFields([]);
				return;
			}
			const propertyKeys = Object.keys(properties);
			const folderTokens = originalShapefiles[0].name.split("/");
			const folderOptions: string[] = [];
			for (let folderLevel = 1; folderLevel <= folderTokens.length; folderLevel++) {
				folderOptions.push("Folder Level " + folderLevel);
			}
			setShapefileFields(() => [...folderOptions, ...propertyKeys]);
		};

		getShapeData();
	}, [originalShapefiles]);

	useEffect(() => {
		// React advises to declare the async function directly inside useEffect
		async function getData() {
			try {
				if (userContext == undefined || !userContext.id) {
					Sentry.captureMessage("userContext is undefined: JobSubmissionForm.tsx", "info");
					toast.error(INVALID_USERCONTEXT);
					return;
				}
				const user = await API.Data.Contacts.get.byId(userContext.id);
				setUser(user);
				const importOptions = await getImportOptions(user, selectedCompany, selectedBranches);
				const companyRecords = await API.Data.Companies.get.byContact(user);

				setImportOptions(importOptions);
				if (importOptions.length === 1) {
					setDataImportOption(importOptions[0]);
				}
				// sort in place by company name
				companyRecords.sort((companyA, companyB) => {
					if (companyA.name === "Rogo AG" || companyA.name === "ROGO Software") return -1;
					if (companyB.name === "Rogo AG" || companyB.name === "ROGO Software") return 1;
					return companyA.name.localeCompare(companyB.name);
				});
				setCompanyOptions(companyRecords);
				if (companyRecords.length === 1) {
					const companyRecord = companyRecords[0];
					setSelectedCompany(companyRecord);
					const growers = await API.Data.Growers.get.byCompany(companyRecord);
					const branches = await API.Data.Branches.get.byCompany(companyRecord);
					const deals = await API.Data.Deals.get.byCompany(companyRecord);
					sortDeals(deals);
					setDeals(deals);
					setGrowers(growers);
					setBranches(branches);
				}
				setLastCropOptions(await API.Data.Jobs.get.lastCropOptions());
				setNextCropOptions(await API.Data.Jobs.get.nextCropOptions());
				setLoading(false);
			} catch (error) {
				Oops.handleError(error, "SubmitJobs.JobSubmissionForm.getData");
			}
		}

		// You need to restrict it at some point
		// This is just dummy code and should be replaced by actual
		if (!deals.length) {
			setLoading(true);
			getData();
		}
	}, [userContext]);

	async function onCompanyChanged(company: Company | null) {
		setLoading(true);
		if (company) {
			// Order may be important if things are asynchronously rerendering...
			const branches = await API.Data.Branches.get.byCompany(company);
			setSelectedBranches([]);
			setBranches(branches);

			const growers = await API.Data.Growers.get.byCompany(company);
			setSelectedGrowers([]);
			setGrowers(growers);

			const deals = await API.Data.Deals.get.byCompany(company);
			setSelectedDeals([]);
			setDeals(deals);

			const salespersons = await getSalepersons(company, branches);
			setSalespersons(salespersons);

			setSelectedCompany(company);
		}
		setLoading(false);
	}

	async function getSalepersons(company: Company, branches: Branch[]) {
		const contacts = await API.Data.Contacts.get.byCompany(company);
		contacts.sort((a, b) => a.name.localeCompare(b.name));

		const sortedContacts = contacts.sort((a, b) => {
			// Order:
			// 1. Is Salesperson && Matches Branch
			// 2. Is Salesperson
			// 3. Remaining
			const aIsSales = a.isSalesperson;
			const bIsSales = b.isSalesperson;
			const branchIds = branches.map((b) => b.id);

			const aHasBranch =
				a.branchIds?.some((id) => branchIds.includes(id)) ||
				a.salespersonBranchIds?.some((id) => branchIds.includes(id));
			const bHasBranch =
				b.branchIds?.some((id) => branchIds.includes(id)) ||
				b.salespersonBranchIds?.some((id) => branchIds.includes(id));

			if (aIsSales !== bIsSales) return bIsSales ? 1 : -1;
			if (aHasBranch !== bHasBranch) return bHasBranch ? 1 : -1;
			return 0;
		});
		return sortedContacts;
	}

	function sortDeals(deals: Deal[]) {
		deals.sort((dealA, dealB) => {
			return dealA.density - dealB.density;
		});
	}

	function DataImportSelection() {
		return (
			<Box display="flex" justifyContent="center" width="100%" margin={2}>
				<ButtonGroup>
					{importOptions.map((option, index) => (
						<Button
							key={index}
							variant={dataImportOption === option ? "contained" : "outlined"}
							onClick={() => {
								setDataImportOption(option);
							}}
						>
							{option.icon}
							<span className="hide-on-mobile-screen">{user?.isDev ? option.superUserLabel : option.label}</span>
							<span className="show-on-mobile-screen">
								{user?.isSuperUser ? option.superUserShortLabel : option.shortLabel}
							</span>
						</Button>
					))}
				</ButtonGroup>
			</Box>
		);
	}

	function DataImportView() {
		const deal = selectedDeals[0];
		if (!dataImportOption) return <></>;

		switch (dataImportOption.id) {
			case ImportOptionChoices.SingleUpload.id:
				return (
					<ManualEntryForm
						isZone={isZone}
						growers={growers}
						selectedDeal={selectedDeals[0]}
						allDeals={deals}
						// companyRecordId={selectedCompany?.id || ''}
						dealIds={[deal.id]}
						depth={deal.depth}
						density={deal.density}
						testPackage={deal.testPackage || "No test package specified"}
						frequencyOfMicros={deal.frequencyOfMicros}
						company={selectedCompany}
						selectedBranches={selectedBranches}
						branches={branches}
						lastCropOptions={lastCropOptions}
						nextCropOptions={nextCropOptions}
						salespersons={salespersons}
					/>
				);
			case ImportOptionChoices.BulkUpload.id:
				return (
					<CSVImportForm
						selectedDeals={selectedDeals}
						selectedBranches={selectedBranches}
						selectedCompany={selectedCompany}
						setSelectableFiles={(groupKey: string, files: File[]) => {
							setSelectableFiles((prev) => {
								const newFiles = prev.concat(files);
								return newFiles;
							});
						}}
						deals={deals}
					/>
				);
			case ImportOptionChoices.BulkUpload2.id:
				return (
					<GeoDataImportForm
						selectedDeals={selectedDeals}
						selectedBranches={selectedBranches}
						selectedCompany={selectedCompany}
						deals={deals}
						growers={growers}
						user={user}
						salespersons={salespersons}
						branches={branches}
						lastCropOptions={lastCropOptions}
						nextCropOptions={nextCropOptions}
					/>
				);
			case ImportOptionChoices.Proagrica.id:
				return (
					<ProagricaImportForm
						selectedBranches={selectedBranches}
						deals={deals}
						selectedDeals={selectedDeals}
						user={user}
						selectedCompany={selectedCompany}
						salespersons={salespersons}
						branches={branches}
						lastCropOptions={lastCropOptions}
						nextCropOptions={nextCropOptions}
					/>
				);
			case ImportOptionChoices.Agrian.id:
				return (
					<External1ImportForm
						selectedBranches={selectedBranches}
						deals={deals}
						selectedDeals={selectedDeals}
						user={user}
						selectedCompany={selectedCompany}
						salespersons={salespersons}
						branches={branches}
						lastCropOptions={lastCropOptions}
						nextCropOptions={nextCropOptions}
					/>
				);
		}
	}

	return (
		<div className="submit-page">
			<Grid container maxWidth="md" spacing={1}>
				{/* Add spacing component here */}
				<Grid item width={"100%"} height="20px"></Grid>

				{/* Company Selection */}
				{companyOptions.length > 1 && (
					<CustomAutocomplete
						optionLabelField="name"
						loading={loading}
						options={companyOptions}
						selectedOption={selectedCompany}
						onChange={onCompanyChanged}
						label="Choose your company"
						// tooltip="Select a company to submit jobs for. This is only for ROGO employees"
					/>
				)}
				{loading && (
					<Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100vh">
						<LoadingComponent />
					</Box>
				)}

				{/* Branch Selection */}
				{
					selectedCompany && branches.length >= 1 && (
						<CustomAutocomplete
							optionLabelField="name"
							loading={loading}
							options={branches}
							selectedOption={selectedBranches.length ? selectedBranches[0] : null}
							onChange={(value) => {
								setSelectedDeals([]);
								setSelectedBranches(value ? [value] : []);
							}}
							optionFormatter={(option) => option.name}
							label="Choose your branch"
						/>
					)
					// <BranchSelectionOptions />
				}

				{/* Select Sampling Configuration */}
				{selectedCompany && (branches.length === 0 || selectedBranches.length >= 1) && (
					<DealSelectionAutocomplete
						deals={deals}
						selectedDeals={selectedDeals}
						loading={loading}
						setSelectedDeals={setSelectedDeals}
					/>
				)}

				{/* Chooose Data Import Method */}
				{selectedCompany &&
					(branches.length === 0 || selectedBranches.length >= 1) &&
					selectedDeals.length >= 1 &&
					importOptions.length > 1 && (
						<>
							<DataImportSelection />
						</>
					)}
			</Grid>
			{selectedCompany &&
				(branches.length === 0 || selectedBranches.length >= 1) &&
				selectedDeals.length >= 1 &&
				dataImportOption && (
					<>
						<DataImportView />
					</>
				)}
		</div>
	);
}

function configContains(company: Company, optionSubstring: string): boolean {
	return (
		company.portalConfigurationOptions.filter((option) => option.toLowerCase().includes(optionSubstring.toLowerCase()))
			.length > 0
	);
}

export function hasProagrica(company: Company): boolean {
	return configContains(company, "proagrica");
}

export function hasAgrian(company: Company, branches: Branch[]): boolean {
	const companyHasAgrianOrgID = (company?.externalLocationId?.length ?? 0) > 0;
	const branchHasAgrianOrgID = branches.some((branch) => (branch?.externalLocationId?.length ?? 0) > 0);
	const hasAgrianOrgID = companyHasAgrianOrgID || branchHasAgrianOrgID;

	return configContains(company, "agrian") && hasAgrianOrgID;
}

export async function getImportOptions(
	user: Contact,
	company: Company | null,
	branches: Branch[],
): Promise<ImportOption[]> {
	if (!company) {
		company = (await API.Data.Companies.get.byContact(user))[0];
	}
	const importOptions: ImportOption[] = [ImportOptionChoices.SingleUpload];

	if (user.isSuperUser || company.portalConfigurationOptions.includes("Import: CSV + SHP")) {
		importOptions.push(ImportOptionChoices.BulkUpload);
	}
	if (user.isSuperUser || company.portalConfigurationOptions.includes("Import: SHP")) {
		importOptions.push(ImportOptionChoices.BulkUpload2);
	}
	if (user.isSuperUser || hasProagrica(company)) {
		importOptions.push(ImportOptionChoices.Proagrica);
	}
	if (user.isSuperUser || hasAgrian(company, branches)) {
		importOptions.push(ImportOptionChoices.Agrian);
	}

	const uniqueOptions = [...new Set(importOptions)];
	return uniqueOptions;
}
